import { http } from '~/src/services/requests';
import type {
  FieldMappingOptions,
  FieldMappingData,
  FieldMappingResponse,
  FieldMappingSnapshotData,
  FieldMappingSnapshotResponse,
} from '../types';
import {
  getFieldMappingRelativeBaseUrl,
  getFieldMappingRelativeUrl,
  getSourceFieldsRelativeUrl,
  getBulkApproveRelativeUrl,
  getDeleteBySourceAttributeRelativeUrl,
  getFieldMappingOverrideRelativeUrl,
  getFieldMappingSnapshotRelativeUrl,
  getCoreFieldModifiedRelativeUrl,
} from '../urls';
import type { EntityType } from '~/src/entities/project/types';

export type FieldMappingOverrideSubjectType = 'matter' | 'contact';

export const getFieldMappingOverrideSubjectType = (
  isRoleCard: boolean,
): FieldMappingOverrideSubjectType => (isRoleCard ? 'contact' : 'matter');

export type SetFieldMappingOverrideParams = {
  orgFprint: string;
  fieldMappingSubjectType: FieldMappingOverrideSubjectType;
  fieldMappingSubjectId: number;
  fieldMappingId: number;
  isOverride: boolean;
};

function fieldMappingService() {
  return {
    getSourceFieldDropdownOptions: (
      orgFprint: string,
      projectId: number,
    ): Promise<FieldMappingOptions> => {
      return http.innerSendAuthenticated(
        {
          relativePath: getSourceFieldsRelativeUrl(orgFprint, projectId),
          method: 'GET',
        },
        true,
      );
    },

    createFieldMapping: (
      orgFprint: string,
      projectId: number,
      data: FieldMappingData,
    ): Promise<FieldMappingResponse> => {
      return http.innerSendAuthenticated(
        {
          relativePath: getFieldMappingRelativeBaseUrl(orgFprint, projectId),
          method: 'POST',
          body: data,
        },
        true,
      );
    },

    updateFieldMapping: (
      orgFprint: string,
      projectId: number,
      mappingId: number,
      data: Partial<FieldMappingData>,
    ): Promise<FieldMappingResponse> => {
      return http.innerSendAuthenticated(
        {
          relativePath: getFieldMappingRelativeUrl(
            orgFprint,
            projectId,
            mappingId,
          ),
          method: 'PATCH',
          body: data,
        },
        true,
      );
    },

    deleteFieldMapping: (
      orgFprint: string,
      projectId: number,
      mappingId: number,
      clearValue?: boolean,
    ): Promise<void> => {
      const baseUrl = getFieldMappingRelativeUrl(
        orgFprint,
        projectId,
        mappingId,
      );
      const url = clearValue ? `${baseUrl}?clear_value=true` : baseUrl;

      return http.innerSendAuthenticated(
        {
          relativePath: url,
          method: 'DELETE',
        },
        false,
      );
    },

    bulkApprove: (
      orgFprint: string,
      projectId: number,
      mappingIds: number[],
    ): Promise<void> => {
      return http.innerSendAuthenticated(
        {
          relativePath: getBulkApproveRelativeUrl(orgFprint, projectId),
          method: 'POST',
          body: { mapping_ids: mappingIds },
        },
        true,
      );
    },

    deleteMappingsBySourceAttribute: (
      orgFprint: string,
      projectId: number,
      sourceField: string,
    ): Promise<{ deleted_count: number }> => {
      return http.innerSendAuthenticated(
        {
          relativePath: getDeleteBySourceAttributeRelativeUrl(
            orgFprint,
            projectId,
          ),
          method: 'DELETE',
          body: { source_field: sourceField },
        },
        true,
      );
    },

    setFieldMappingOverride: ({
      orgFprint,
      fieldMappingSubjectType,
      fieldMappingSubjectId,
      fieldMappingId,
      isOverride,
    }: SetFieldMappingOverrideParams): Promise<unknown> => {
      return http.innerSendAuthenticated(
        {
          relativePath: getFieldMappingOverrideRelativeUrl(orgFprint),
          method: 'POST',
          body: {
            subject_type: fieldMappingSubjectType,
            subject_id: fieldMappingSubjectId,
            field_mapping_id: fieldMappingId,
            is_override: isOverride,
          },
        },
        true,
      );
    },

    saveFieldMappingSnapshot: (
      orgFprint: string,
      projectId: number,
      data: FieldMappingSnapshotData,
    ): Promise<FieldMappingSnapshotResponse | void> => {
      return http.innerSendAuthenticated(
        {
          relativePath: getFieldMappingSnapshotRelativeUrl(
            orgFprint,
            projectId,
          ),
          method: 'POST',
          body: data,
        },
        true,
      );
    },

    markCoreFieldModified: (
      orgFprint: string,
      entityType: EntityType.CONTACT | EntityType.MATTER,
      entityId: number,
      fieldName: string,
    ): Promise<{ success: boolean }> => {
      return http.innerSendAuthenticated(
        {
          relativePath: getCoreFieldModifiedRelativeUrl(orgFprint),
          method: 'POST',
          body: {
            entity_type: entityType,
            entity_id: entityId,
            field_name: fieldName,
          },
        },
        true,
      );
    },
  };
}

export default fieldMappingService();
